import { createContext, useContext, useEffect, useState } from 'react';

const LOGIN_RETURN_KEY = 'cabBookingLoginReturn';

const defaultBooking = {
  tripType: 'instant',          // 'instant' | 'rental' | 'outstation'
  rideMode: 'one_way',          // used inside Outstation tab
  pickup: { id: '', name: '', latitude: null, longitude: null },
  dropoff: { id: '', name: '', latitude: null, longitude: null },
  date: new Date().toISOString().split('T')[0],
  dropDate: '',
  dropTime: '',
  returnDate: '',
  pickupTime: '',
  selectedPackage: null,
  selectedVehicle: null,
  passenger: { name: '', phone: '', email: '', passengers: 1, instructions: '' },
  booking: null,                // set after createBooking()
  payment: null,                // set after createPayment()
  paymentVerification: null,
};

const BookingContext = createContext({
  booking: defaultBooking,
  updateBooking: () => {},
  resetBooking: () => {},
});

export const useBooking = () => {
  const ctx = useContext(BookingContext);
  if (!ctx) {
    throw new Error('useBooking must be used inside <BookingProvider>');
  }
  return ctx;
};

export const BookingProvider = ({ children }) => {
  const [booking, setBooking] = useState(() => {
    const savedBooking = sessionStorage.getItem(LOGIN_RETURN_KEY);
    if (!savedBooking) return defaultBooking;

    try {
      const parsedBooking = JSON.parse(savedBooking);
      if (!parsedBooking || typeof parsedBooking !== 'object' || Array.isArray(parsedBooking)) {
        throw new Error('Saved cab booking data is invalid.');
      }
      return { ...defaultBooking, ...parsedBooking };
    } catch (error) {
      console.error('Failed to restore cab booking after login:', error);
      return defaultBooking;
    }
  });

  useEffect(() => {
    sessionStorage.removeItem(LOGIN_RETURN_KEY);
  }, []);

  const updateBooking = (key, value) =>
    setBooking((prev) => ({ ...prev, [key]: value }));

  const updateNested = (parentKey, childKey, value) =>
    setBooking((prev) => ({
      ...prev,
      [parentKey]: { ...prev[parentKey], [childKey]: value },
    }));

  const resetBooking = () => setBooking(defaultBooking);

  return (
    <BookingContext.Provider
      value={{ booking, updateBooking, updateNested, resetBooking }}
    >
      {children}
    </BookingContext.Provider>
  );
};